import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Loader2, Save, MessageSquareDashed } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import ImageUploader from '../ImageUploader';
import { resolveImageUrl } from '../../utils/imageUrl';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PAGES = [
    { value: 'all', label: 'All pages' },
    { value: '/', label: 'Homepage' },
    { value: '/events', label: 'Events' },
    { value: '/library', label: 'Library' },
    { value: '/pillars', label: 'Pillars' },
    { value: '/membership', label: 'Membership' },
    { value: '/forum', label: 'Forum' },
    { value: '/connections', label: 'Connections' },
    { value: '/dashboard', label: 'Dashboard' },
];
const FREQ = { once: 'Only once per visitor', daily: 'Once a day', session: 'Once per visit', every: 'Every page view' };
const AUDIENCE = { everyone: 'Everyone', guests: 'Visitors (not signed in)', members: 'Members only' };
const EMPTY = { title: '', body: '', image_url: '', link_url: '', button_label: '', pages: ['all'], frequency: 'daily', audience: 'everyone', start_at: '', end_at: '', delay_seconds: 2, active: true };

const toLocalInput = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};
const toIso = (local) => (local ? new Date(local).toISOString() : null);
const fieldCls = 'w-full bg-white border border-[#E5E0D8] px-3 py-2 rounded-sm text-sm';
const Label = ({ children }) => <label className="block font-sans text-xs uppercase tracking-widest text-[#4A4A68] mb-1">{children}</label>;

function PopupForm({ form, setForm }) {
    const set = (patch) => setForm({ ...form, ...patch });
    const togglePage = (v) => {
        if (v === 'all') return set({ pages: ['all'] });
        const rest = form.pages.filter(p => p !== 'all');
        const next = rest.includes(v) ? rest.filter(p => p !== v) : [...rest, v];
        set({ pages: next.length ? next : ['all'] });
    };
    return (
        <div className="space-y-4">
            <div><Label>Title</Label><input className={fieldCls} value={form.title} onChange={e => set({ title: e.target.value })} placeholder="Take the quiz" data-testid="popup-title-input" /></div>
            <div><Label>Text</Label><textarea className={fieldCls} rows={3} value={form.body} onChange={e => set({ body: e.target.value })} placeholder="Discover which map speaks to you…" data-testid="popup-body-input" /></div>
            <ImageUploader value={form.image_url} onChange={(url) => set({ image_url: url })} testIdPrefix="popup-image" label="Image (optional, clickable)" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Link (quiz or action)</Label><input className={fieldCls} value={form.link_url} onChange={e => set({ link_url: e.target.value })} placeholder="https://… or /events" data-testid="popup-link-input" /></div>
                <div><Label>Button text</Label><input className={fieldCls} value={form.button_label} onChange={e => set({ button_label: e.target.value })} placeholder="Take the quiz" data-testid="popup-button-input" /></div>
            </div>
            <div>
                <Label>Show on</Label>
                <div className="flex flex-wrap gap-2" data-testid="popup-pages">
                    {PAGES.map(p => (
                        <button type="button" key={p.value} onClick={() => togglePage(p.value)}
                            className={`px-3 py-1 text-xs rounded-full border transition-colors ${form.pages.includes(p.value) ? 'bg-[#5B2D8E] text-white border-[#5B2D8E]' : 'bg-white text-[#4A4A68] border-[#E5E0D8]'}`}
                            data-testid={`popup-page-${p.value === '/' ? 'home' : p.value.replace(/\//g, '') || 'all'}`}>
                            {p.label}
                        </button>
                    ))}
                </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>How often</Label>
                    <select className={fieldCls} value={form.frequency} onChange={e => set({ frequency: e.target.value })} data-testid="popup-frequency-select">
                        {Object.entries(FREQ).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                </div>
                <div><Label>Who sees it</Label>
                    <select className={fieldCls} value={form.audience} onChange={e => set({ audience: e.target.value })} data-testid="popup-audience-select">
                        {Object.entries(AUDIENCE).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                </div>
                <div><Label>Start (optional)</Label><input type="datetime-local" className={fieldCls} value={form.start_at} onChange={e => set({ start_at: e.target.value })} data-testid="popup-start-input" /></div>
                <div><Label>End (optional)</Label><input type="datetime-local" className={fieldCls} value={form.end_at} onChange={e => set({ end_at: e.target.value })} data-testid="popup-end-input" /></div>
                <div><Label>Delay before showing (seconds)</Label><input type="number" min={0} max={60} className={fieldCls} value={form.delay_seconds} onChange={e => set({ delay_seconds: e.target.value })} data-testid="popup-delay-input" /></div>
                <label className="flex items-center gap-2 mt-5 text-sm text-[#1A1A2E]">
                    <input type="checkbox" className="w-4 h-4 accent-[#5B2D8E]" checked={form.active} onChange={e => set({ active: e.target.checked })} data-testid="popup-active-checkbox" />
                    Active
                </label>
            </div>
        </div>
    );
}

const Stat = ({ label, s, testId }) => (
    <span className="text-xs text-[#4A4A68]" data-testid={testId}>
        <strong className="text-[#1A1A2E]">{s.total}</strong> {label} <span className="text-[#4A4A68]/70">({s.unique} {s.unique === 1 ? 'person' : 'people'})</span>
    </span>
);

function PopupStats({ popup, onReset }) {
    const st = popup.stats || {};
    const z = { total: 0, unique: 0 };
    const view = st.view || z, click = st.click || z, close = st.close || z;
    const rate = view.unique ? Math.round((click.unique / view.unique) * 100) : 0;
    return (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2" data-testid={`popup-stats-${popup.id}`}>
            <Stat label="seen" s={view} testId={`popup-stat-view-${popup.id}`} />
            <Stat label="clicked" s={click} testId={`popup-stat-click-${popup.id}`} />
            <Stat label="closed" s={close} testId={`popup-stat-close-${popup.id}`} />
            <span className="text-xs px-2 py-0.5 rounded-sm bg-[#D4AF37]/20 text-[#1A1A2E]" data-testid={`popup-stat-rate-${popup.id}`}>
                {rate}% click rate
            </span>
            {view.total > 0 && (
                <button onClick={onReset} className="text-xs text-[#4A4A68] underline hover:text-[#5B2D8E]" data-testid={`popup-stats-reset-${popup.id}`}>
                    Reset
                </button>
            )}
        </div>
    );
}

export default function PopupsPanel() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try {
            const { data } = await axios.get(`${API_URL}/api/admin/popups`, { withCredentials: true });
            setItems(data || []);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Could not load pop-ups');
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const startNew = () => { setEditingId(null); setForm(EMPTY); setOpen(true); };
    const startEdit = (p) => {
        setEditingId(p.id);
        setForm({ ...EMPTY, ...p, start_at: toLocalInput(p.start_at), end_at: toLocalInput(p.end_at) });
        setOpen(true);
    };

    const save = async () => {
        setSaving(true);
        const payload = { ...form, start_at: toIso(form.start_at), end_at: toIso(form.end_at), delay_seconds: Number(form.delay_seconds) || 0 };
        try {
            if (editingId) await axios.put(`${API_URL}/api/admin/popups/${editingId}`, payload, { withCredentials: true });
            else await axios.post(`${API_URL}/api/admin/popups`, payload, { withCredentials: true });
            toast.success('Pop-up saved');
            setOpen(false);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Could not save pop-up');
        } finally {
            setSaving(false);
        }
    };

    const remove = async (p) => {
        if (!window.confirm(`Delete pop-up "${p.title || 'Untitled'}"?`)) return;
        try {
            await axios.delete(`${API_URL}/api/admin/popups/${p.id}`, { withCredentials: true });
            setItems(items.filter(i => i.id !== p.id));
        } catch (err) {
            toast.error('Could not delete pop-up');
        }
    };

    const resetStats = async (p) => {
        if (!window.confirm(`Reset stats for "${p.title || 'Untitled'}"?`)) return;
        try {
            await axios.delete(`${API_URL}/api/admin/popups/${p.id}/stats`, { withCredentials: true });
            load();
        } catch (err) {
            toast.error('Could not reset stats');
        }
    };

    const pageLabels = (pages) => (pages || ['all']).map(v => PAGES.find(p => p.value === v)?.label || v).join(', ');

    return (
        <div data-testid="popups-panel">
            <div className="flex items-center justify-between mb-6 gap-4">
                <div>
                    <h2 className="font-serif text-3xl text-[#1A1A2E]">Pop-ups</h2>
                    <p className="text-sm text-[#4A4A68]">Announce the quiz or a new action when people open the site.</p>
                </div>
                <button onClick={startNew} className="btn-primary flex items-center gap-2 shrink-0" data-testid="popup-new-btn">
                    <Plus className="w-4 h-4" /> New pop-up
                </button>
            </div>

            {loading ? (
                <Loader2 className="w-6 h-6 animate-spin text-[#5B2D8E]" />
            ) : items.length === 0 ? (
                <div className="text-center py-16 border border-dashed border-[#E5E0D8] rounded-sm text-[#4A4A68]" data-testid="popups-empty">
                    <MessageSquareDashed className="w-8 h-8 mx-auto mb-2 text-[#D8B4E2]" />
                    No pop-ups yet.
                </div>
            ) : (
                <div className="space-y-3">
                    {items.map(p => (
                        <div key={p.id} className="flex items-center gap-4 p-4 bg-white border border-[#E5E0D8] rounded-sm" data-testid={`popup-row-${p.id}`}>
                            {p.image_url
                                ? <img src={resolveImageUrl(p.image_url)} alt="" className="w-16 h-16 object-cover rounded-sm shrink-0" />
                                : <div className="w-16 h-16 bg-[#F9F6F0] rounded-sm shrink-0" />}
                            <div className="flex-1 min-w-0">
                                <p className="font-serif text-lg text-[#1A1A2E] truncate">{p.title || 'Untitled pop-up'}</p>
                                <p className="text-xs text-[#4A4A68] truncate">{pageLabels(p.pages)} · {FREQ[p.frequency]} · {AUDIENCE[p.audience]}</p>
                                <PopupStats popup={p} onReset={() => resetStats(p)} />
                            </div>
                            <span className={`text-xs px-2 py-1 rounded-sm ${p.active ? 'bg-[#5B2D8E]/10 text-[#5B2D8E]' : 'bg-[#E5E0D8] text-[#4A4A68]'}`} data-testid={`popup-status-${p.id}`}>
                                {p.active ? 'Active' : 'Off'}
                            </span>
                            <button onClick={() => startEdit(p)} className="p-2 text-[#4A4A68] hover:text-[#5B2D8E]" aria-label="Edit" data-testid={`popup-edit-${p.id}`}><Edit2 className="w-4 h-4" /></button>
                            <button onClick={() => remove(p)} className="p-2 text-[#4A4A68] hover:text-red-600" aria-label="Delete" data-testid={`popup-delete-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="w-[calc(100%-2rem)] sm:max-w-xl bg-white max-h-[90vh] overflow-y-auto" data-testid="popup-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-2xl text-[#1A1A2E]">{editingId ? 'Edit pop-up' : 'New pop-up'}</DialogTitle>
                    </DialogHeader>
                    <PopupForm form={form} setForm={setForm} />
                    <button onClick={save} disabled={saving} className="btn-primary w-full mt-4 flex items-center justify-center gap-2" data-testid="popup-save-btn">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save pop-up
                    </button>
                </DialogContent>
            </Dialog>
        </div>
    );
}
